import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { 
  ClipboardList, 
  Star, 
  Clock, 
  User,
  CheckCircle,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import { usePullToRefresh, RefreshIndicator } from '../../hooks/usePullToRefresh';

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Management'
};

const StaffDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const response = await api.get('/staff/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const { refreshing, onTouchStart, onTouchEnd } = usePullToRefresh(fetchData);

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff Dashboard">
      <div className="space-y-6 lg:space-y-8" data-testid="staff-dashboard" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <RefreshIndicator refreshing={refreshing} />
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          <Link to="/staff/enrollments" data-testid="stat-pending-enrollments">
            <Card className="border border-slate-200 hover:border-amber-300 transition-colors cursor-pointer">
              <CardContent className="p-3 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm font-medium text-slate-500">Pending</p>
                    <p className="font-heading text-2xl lg:text-3xl font-bold text-amber-600 mt-1">
                      {stats?.pending_enrollments || 0}
                    </p>
                  </div>
                  <div className="p-2 lg:p-3 rounded-lg bg-amber-100">
                    <ClipboardList className="h-5 w-5 lg:h-6 lg:w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/staff/trials" data-testid="stat-pending-trials">
            <Card className="border border-slate-200 hover:border-blue-300 transition-colors cursor-pointer">
              <CardContent className="p-3 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm font-medium text-slate-500">Trials</p>
                    <p className="font-heading text-2xl lg:text-3xl font-bold text-blue-600 mt-1">
                      {stats?.pending_trials || 0}
                    </p>
                  </div>
                  <div className="p-2 lg:p-3 rounded-lg bg-blue-100">
                    <Star className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/staff/hours" data-testid="stat-hours-month">
            <Card className="border border-slate-200 hover:border-green-300 transition-colors cursor-pointer">
              <CardContent className="p-3 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm font-medium text-slate-500">Hours/Mo</p>
                    <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                      {stats?.hours_this_month?.toFixed(1) || 0}
                    </p>
                    {stats?.pending_hours > 0 && (
                      <p className="text-[10px] lg:text-xs text-amber-600 mt-0.5">{stats.pending_hours.toFixed(1)} pending</p>
                    )}
                  </div>
                  <div className="p-2 lg:p-3 rounded-lg bg-green-100">
                    <Clock className="h-5 w-5 lg:h-6 lg:w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/staff/hours" data-testid="stat-approved-hours">
            <Card className="border border-slate-200 hover:border-green-300 transition-colors cursor-pointer">
              <CardContent className="p-3 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm font-medium text-slate-500">Approved</p>
                    <p className="font-heading text-2xl lg:text-3xl font-bold text-green-600 mt-1">
                      {stats?.approved_hours?.toFixed(1) || 0}
                    </p>
                  </div>
                  <div className="p-2 lg:p-3 rounded-lg bg-green-500">
                    <CheckCircle className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Assigned Duties */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-blue-500" />
              My Assigned Duties
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 lg:p-6">
            {!stats?.assigned_duties || stats.assigned_duties.length === 0 ? (
              <div className="text-center text-slate-500 py-4">
                <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p>No duties assigned yet</p>
                <p className="text-sm">Contact admin to assign your duties</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {stats.assigned_duties.map((duty) => (
                  <span
                    key={duty}
                    className="px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-medium"
                  >
                    {DUTY_LABELS[duty] || duty}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
          <Link to="/staff/hours" data-testid="quick-log-hours">
            <Card className="card-hover border border-slate-200 cursor-pointer group">
              <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
                <div className="p-2 lg:p-3 rounded-lg bg-green-100 group-hover:bg-green-500 transition-colors">
                  <Clock className="h-5 w-5 lg:h-6 lg:w-6 text-green-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm lg:text-base">Log Hours</p>
                  <p className="text-xs lg:text-sm text-slate-500 hidden sm:block">Submit your working hours</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/staff/enrollments" data-testid="quick-enrollments">
            <Card className="card-hover border border-slate-200 cursor-pointer group">
              <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
                <div className="p-2 lg:p-3 rounded-lg bg-amber-100 group-hover:bg-amber-500 transition-colors">
                  <ClipboardList className="h-5 w-5 lg:h-6 lg:w-6 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm lg:text-base">Manage Enrollments</p>
                  <p className="text-xs lg:text-sm text-slate-500 hidden sm:block">Review pending enrollments</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/staff/profile" data-testid="quick-profile">
            <Card className="card-hover border border-slate-200 cursor-pointer group">
              <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
                <div className="p-2 lg:p-3 rounded-lg bg-blue-100 group-hover:bg-blue-500 transition-colors">
                  <User className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm lg:text-base">My Profile</p>
                  <p className="text-xs lg:text-sm text-slate-500 hidden sm:block">Update your information</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StaffDashboard;
